import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControl,
    FormControlLabel,
    FormLabel,
    Checkbox,
    CircularProgress,
    Alert,
    Switch,
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/admin';
import {
    useInventarKonfiguraceQuery,
    useUpdateInventarKonfiguraceMutation,
} from '../../api/JsdhNotifikaceService/service/InventarMutations';

const InventarSettingsPage: React.FC = () => {
    const navigate = useNavigate();
    const { data: cfg, isLoading } = useInventarKonfiguraceQuery();
    const updateMutation = useUpdateInventarKonfiguraceMutation();

    const [zdroj, setZdroj] = useState('EXTERNI');
    const [format, setFormat] = useState('{rok}/INV/{seq:04d}');
    const [sekvence, setSekvence] = useState('ROK');
    const [povinne, setPovinne] = useState(false);
    const [oopNotifikace, setOopNotifikace] = useState(false);

    useEffect(() => {
        if (cfg) {
            setZdroj(cfg.cisloZdroj);
            setFormat(cfg.cisloFormat);
            setSekvence(cfg.cisloSekvencePer);
            setPovinne(cfg.cisloPovinne);
            setOopNotifikace(cfg.notifikaceOopEnabled ?? false);
        }
    }, [cfg]);

    const handleSave = () => {
        updateMutation.mutate({
            cisloZdroj: zdroj,
            cisloFormat: format,
            cisloSekvencePer: sekvence,
            cisloPovinne: povinne,
            notifikaceOopEnabled: oopNotifikace,
        });
    };

    if (isLoading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <PageHeader
                title="Nastavení inventáře"
                icon={<BackIcon sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/inventar')} />}
            />

            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Inventární čísla</Typography>

                <FormControl sx={{ mb: 3 }}>
                    <FormLabel>Kdo přiděluje inventární čísla?</FormLabel>
                    <RadioGroup value={zdroj} onChange={(e) => setZdroj(e.target.value)}>
                        <FormControlLabel
                            value="EXTERNI"
                            control={<Radio />}
                            label="Externí (zadává účetní ručně) — doporučeno"
                        />
                        <FormControlLabel
                            value="AUTO"
                            control={<Radio />}
                            label="Automaticky (systém generuje podle šablony)"
                        />
                        <FormControlLabel
                            value="VOLITELNE"
                            control={<Radio />}
                            label="Volitelné (systém navrhne, lze přepsat)"
                        />
                    </RadioGroup>
                </FormControl>

                {(zdroj === 'AUTO' || zdroj === 'VOLITELNE') && (
                    <Box sx={{ mb: 3, pl: 4 }}>
                        <TextField
                            label="Formát automatického čísla"
                            fullWidth
                            value={format}
                            onChange={(e) => setFormat(e.target.value)}
                            helperText={`Placeholdery: {rok}, {kategorie}, {seq:04d}. Náhled: ${cfg?.nahledCisla ?? '...'}`}
                            sx={{ mb: 2 }}
                        />
                        <FormControl>
                            <FormLabel>Reset sekvence</FormLabel>
                            <RadioGroup value={sekvence} onChange={(e) => setSekvence(e.target.value)} row>
                                <FormControlLabel value="ROK" control={<Radio />} label="Reset každý rok" />
                                <FormControlLabel value="GLOBAL" control={<Radio />} label="Jedna řada napříč lety" />
                            </RadioGroup>
                        </FormControl>
                    </Box>
                )}

                <FormControlLabel
                    control={<Checkbox checked={povinne} onChange={(e) => setPovinne(e.target.checked)} />}
                    label="Vyžadovat inventární číslo při vytvoření položky"
                    sx={{ display: 'block', mb: 2 }}
                />

                {zdroj === 'EXTERNI' && !povinne && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Položky bez inventárního čísla budou mít stav "Čeká na účetní" — číslo můžete doplnit hromadně později.
                    </Alert>
                )}
            </Paper>

            <Paper sx={{ p: 3, mb: 2 }}>
                <Typography variant="h6" gutterBottom>Revize OOP</Typography>
                <FormControlLabel
                    control={<Switch checked={oopNotifikace} onChange={(e) => setOopNotifikace(e.target.checked)} />}
                    label="Upozorňovat na expirace revizí OOP"
                    sx={{ display: 'block' }}
                />
            </Paper>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button onClick={() => navigate('/admin/inventar')}>Zpět</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={updateMutation.isPending}
                >
                    Uložit změny
                </Button>
            </Box>
        </Box>
    );
};

export default InventarSettingsPage;
